import { Box, Icon, Image, Spinner, Stack, Text } from '@chakra-ui/react';
import { ImagePlus } from 'lucide-react';
import { useContext, useMemo } from 'react';

import { FileUploadContext } from './FileUploadContext';

export function ImageUploadDisplay({
  isDisabled,
  imageUrl = null,
  label,
}: {
  isDisabled?: boolean;
  imageUrl?: string | null;
  label?: string;
}) {
  const context = useContext(FileUploadContext);
  const isUploading = context?.hasPendingFileUploads ?? false;
  const uploadProgress = context?.uploadProgress;

  const previewUrl = useMemo(() => {
    if (context?.files && context.files.length > 0) {
      const pendingFile = context.files[context.files.length - 1];
      if (pendingFile && 'previewUrl' in pendingFile) {
        return pendingFile.previewUrl;
      }
    }

    return imageUrl;
  }, [context?.files, imageUrl]);

  if (isUploading) {
    return (
      <Stack bg="white" w="full" h="full" rounded="8px" justifyContent="center" alignItems="center">
        <Spinner size="md" color="fg.accent.emphasis" />
        <Text fontSize="sm" color="fg.accent.emphasis">
          {uploadProgress != null ? `Uploading... ${Math.round(uploadProgress)}%` : 'Uploading...'}
        </Text>
      </Stack>
    );
  }

  if (previewUrl != null) {
    return (
      <Box
        position="relative"
        w="full"
        h="full"
        rounded="8px"
        overflow="hidden"
        _hover={{
          '& .hover-overlay': {
            opacity: 1,
          },
        }}
      >
        <Image src={previewUrl} alt="" w="full" h="full" objectFit="cover" rounded="8px" />
        {isDisabled !== true ? (
          <Box
            className="hover-overlay"
            position="absolute"
            top="0"
            left="0"
            right="0"
            bottom="0"
            bg="rgba(0, 0, 0, 0.6)"
            display="flex"
            alignItems="center"
            justifyContent="center"
            flexDirection="column"
            opacity="0"
            transition="opacity 0.2s ease-in-out"
            rounded="8px"
          >
            <Icon as={ImagePlus} boxSize={6} color="bg.surface" />
            <Text fontSize="sm" color="bg.surface" mt={1}>
              {label ?? 'Upload image'}
            </Text>
          </Box>
        ) : null}
      </Box>
    );
  }

  return (
    <Stack
      bg="bg.surface"
      w="full"
      h="full"
      rounded="8px"
      justifyContent="center"
      alignItems="center"
    >
      <Icon
        as={ImagePlus}
        boxSize={6}
        color={isDisabled === true ? 'gray.400' : 'fg.accent.emphasis'}
      />
      {isDisabled !== true ? (
        <Text fontSize="sm" color="fg.accent.emphasis">
          {label ?? 'Add image'}
        </Text>
      ) : null}
    </Stack>
  );
}
